// src/routes/merchants/catalogue/BrandConfirm.tsx
import { useEffect, useId, useRef, useState } from 'react'
import { Button } from '../../../platform/primitives/Button'
import { Checkbox } from '../../../platform/primitives/Checkbox'
import { useSharedHousehold } from '../../household/useHousehold'
import type { PickerMerchant } from '../picker/merchantPickerModel'
import type { Brand, PickExtras } from './catalogueModel'
import { RegionLabel } from './RegionLabel'
import { useAddBrand, type AddBrandResult } from './useCatalogue'
import './brands.css'


function failureCopy(status: number | null, name: string): string {
  if (status === 403) return 'You can’t share with that household any more. Turn off sharing and try again.'
  if (status === 404) return `${name} is no longer in the catalogue.`
  if (status === 409) return `A merchant called “${name}” already exists.`
  return `Couldn’t add ${name}. Try again.`
}

/** After a 409: the merchant the user already has, and the category the add would have suggested. */
type Existing = { merchant: PickerMerchant; suggested: { id: string } | null }

/**
 * "Add Tim Hortons as a merchant" — the one step between a brand and a merchant (plan 2026-10-16, Task 1).
 * Nothing is added until the user presses Add (owner decision C4).
 *
 * - **Share with household** is offered only when there is a household with someone else in it
 *   (`useSharedHousehold`), and starts off. Outside one it does not exist, so a shared add cannot be sent.
 * - **Also set category to <suggested>** is offered only when the caller says the transaction has no
 *   category yet (`offerCategory`) and the brand has a suggestion, and starts on.
 *
 * Add creates the merchant and hands it to `onAdded`, the same path as picking a merchant.
 *
 * **A 409 never creates a duplicate.** The server says a merchant by this name exists; `useAddBrand` finds
 * it, and this step says so and offers "Use <name>" in Add's place. The user's tap assigns the merchant
 * they already have. Any other failure is shown here, and the step stays open.
 *
 * "Also set category" stays on screen after a 409, and **Use reads it as it is when Use is pressed**.
 *
 * Focus moves to the step when it opens. Cancel and a successful Add close it with focus inside, so the
 * caller restores focus on both: it knows what is still on its screen.
 */
export function BrandConfirm({
  brand,
  offerCategory,
  onAdded,
  onCancel,
  addVariant = 'primary',
}: {
  brand: Brand
  offerCategory: boolean
  onAdded: (merchant: PickerMerchant, extras?: PickExtras) => void
  onCancel: () => void
  /** `primary` inside the picker, the dialog's one action; `outline` where the page already has its primary. */
  addVariant?: 'primary' | 'outline'
}) {
  const householdId = useSharedHousehold()
  const add = useAddBrand()
  const [share, setShare] = useState(false)
  const [setCategory, setSetCategory] = useState(true)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const [existing, setExisting] = useState<Existing | null>(null)
  const titleId = useId()
  const root = useRef<HTMLElement>(null)

  useEffect(() => {
    root.current?.focus()
  }, [])

  const category = offerCategory ? brand.suggestedCategory : null
  const extrasFor = (suggested: { id: string } | null): PickExtras | undefined =>
    offerCategory && setCategory && suggested !== null ? { categoryId: suggested.id } : undefined

  // A pick without extras is called as a plain pick is, with the merchant alone.
  const pick = (m: PickerMerchant, extras: PickExtras | undefined) => (extras === undefined ? onAdded(m) : onAdded(m, extras))

  const run = async () => {
    if (busy) return
    setBusy(true)
    setFailure(null)
    const result: AddBrandResult = await add(brand, { householdId: share && householdId !== null ? householdId : null })
    setBusy(false)
    if (!result.ok) {
      setFailure(failureCopy(result.status, brand.name))
      return
    }
    const suggested = result.suggestedCategory ?? brand.suggestedCategory
    if (result.existing) setExisting({ merchant: result.merchant, suggested })
    else pick(result.merchant, extrasFor(suggested))
  }

  return (
    <section ref={root} className="brand-confirm" aria-labelledby={titleId} tabIndex={-1}>
      <h3 id={titleId} className="brand-confirm__title">
        {`Add ${brand.name} as a merchant`} <RegionLabel region={brand.region} />
      </h3>
      {existing === null && householdId !== null && (
        <label className="brand-confirm__option">
          <Checkbox checked={share} onChange={(v) => !busy && setShare(v)} label="Share with household" />
          <span aria-hidden="true">Share with household</span>
        </label>
      )}
      {category !== null && (
        <label className="brand-confirm__option">
          <Checkbox checked={setCategory} onChange={(v) => !busy && setSetCategory(v)} label={`Also set category to ${category.name}`} />
          <span aria-hidden="true">{`Also set category to ${category.name}`}</span>
        </label>
      )}
      {existing !== null && (
        <p role="status" className="brand-confirm__note">
          {`You already have a merchant called “${existing.merchant.name}”.`}
        </p>
      )}
      {failure !== null && (
        <p role="alert" className="brand-confirm__error">
          {failure}
        </p>
      )}
      <div className="brand-confirm__actions">
        <Button type="button" variant="outline" size="form" aria-disabled={busy || undefined} onClick={() => !busy && onCancel()}>
          Cancel
        </Button>
        {existing === null ? (
          <Button type="button" variant={addVariant} size="form" aria-disabled={busy || undefined} onClick={() => void run()}>
            Add
          </Button>
        ) : (
          <Button
            type="button"
            variant={addVariant}
            size="form"
            onClick={() => pick(existing.merchant, extrasFor(existing.suggested))}
          >
            {`Use ${existing.merchant.name}`}
          </Button>
        )}
      </div>
    </section>
  )
}
